import { FunctionsHttpError } from '@supabase/supabase-js';
import type { FunctionInvokeOptions } from '@supabase/supabase-js';
import { report } from '../sentry';
import { supabase } from './auth';
import { looksOffline } from './offline';
import { wipeDevice } from './wipe';

/** Удаление учётной записи родителя: порядок, разбор ответа и правдивые
 *  слова.
 *
 *  Удаление на сервере и стирание устройства — не одна транзакция, и одной
 *  их не сделать: первое живёт в серверной функции, второе — в IndexedDB
 *  этого браузера. Поэтому между ними стоит ПОМЕТКА, переживающая
 *  перезагрузку, и по ней приложение знает, на каком шаге его прервали.
 *  Обещание, которое держит этот модуль: **родитель всегда знает, что
 *  стало, — удалено всё; запись удалена, а данные на устройстве могли
 *  остаться; исход неизвестен, — и никогда не слышит «не получилось»,
 *  когда получилось.**
 *
 *  Таблица состояний и переходов — карточка 3а плана
 *  `docs/superpowers/plans/2026-09-24-reliability-and-debt.md`, редакция 2.
 *  Первая редакция выходила из учётной записи ДО стирания, и это измерено
 *  исполнением как неверное: выход шлёт `SIGNED_OUT`, шлюз входа пытается
 *  вернуть прежнюю сессию, сервер отвечает отказом (пользователя уже нет),
 *  и вместо слов об отказе стирания родитель видел «Войдите снова… Занятия
 *  на этом устройстве целы». А повтор уборки «при запуске, в шлюзе
 *  профилей» после перезагрузки не наступал: сессии нет, шлюз профилей не
 *  монтируется. Отсюда два правила ниже: стирание — ДО выхода, повтор —
 *  над шлюзом входа.
 *
 *  Все исходы разбираются здесь и возвращаются значением; наружу не
 *  бросается ничего. Удаление зовётся из продолжения границы `действие`, и
 *  бросок оттуда граница показала бы своими словами «не получилось» —
 *  ровно тем, что обещание запрещает. */

/** `'started'` — пароль подтверждён, запрос ушёл, чем кончился — неизвестно.
 *  `'confirmed'` — сервер ответил, что запись удалена; устройство, возможно,
 *  ещё не стёрто. */
export type Пометка = 'started' | 'confirmed';

/** Пометка — в `localStorage`, а не в базе и не на строке устройства.
 *
 *  Не в таблице Dexie: `wipeDevice` очищает таблицы, и пометка
 *  «подтверждено» погибла бы в той самой уборке, которую ей надо добить.
 *  Не на строке устройства: её единственный писатель (`saveDevice` в
 *  `src/ui/useTrainer.ts`) перезаписывает строку целиком. Обращение к
 *  `localStorage` бросает в приватном окне — поэтому обёрнуто, как в
 *  `src/ui/localOnlyReminder.ts`.
 *
 *  Тесты читают и снимают пометку ТОЛЬКО через функции ниже: иная верная
 *  реализация хранения (отдельная маленькая база IndexedDB, которую
 *  `wipeDevice` не трогает) обязана проходить те же клетки. Поэтому и
 *  чтение, и запись асинхронные. */
const КЛЮЧ = 'mathpaw.account-deletion';

export async function прочитатьПометку(): Promise<Пометка | null> {
  try {
    const значение = window.localStorage.getItem(КЛЮЧ);
    return значение === 'started' || значение === 'confirmed' ? значение : null;
  } catch {
    return null;
  }
}

async function записатьПометку(значение: Пометка): Promise<void> {
  try {
    window.localStorage.setItem(КЛЮЧ, значение);
  } catch {
    /** Шаг 1 таблицы: без пометки удаление продолжается. Пропадает только
     *  повтор после перезагрузки, а остановиться здесь значило бы не
     *  удалить запись из-за приватного окна. */
  }
}

export async function снятьПометку(): Promise<void> {
  try {
    window.localStorage.removeItem(КЛЮЧ);
  } catch {
    /* Приватное окно: снимать нечего — записать тоже не удалось. */
  }
}

/** Чем кончилось. Значение, а не текст: слова выбирает `текстИсхода`, и
 *  тесты проверяют состояние отдельно от формулировки. */
export type Исход =
  /** Запись удалена, устройство стёрто, выход сделан — дальше экран входа. */
  | 'удалено'
  /** Шаг 2: сервер отказал ДО удаления, запись цела. */
  | 'отказ'
  /** Шаг 3: ответа нет или он такой, что удаление могло случиться. */
  | 'неизвестно'
  /** Шаг 6: запись удалена, а стереть устройство не вышло. */
  | 'данные-остались'
  /** Шаг 8: проверка показала, что запись жива. */
  | 'жива'
  /** Шаг 9: исход неизвестен, а спросить сервер не от чьего имени. */
  | 'незавершено'
  /** Шаг 7: повтор уборки при запуске снова отказал. */
  | 'не-стёрто'
  /** Говорить нечего: пометки нет, или проверка не получила ответа. */
  | 'нечего';

/** Слова родителю по исходу. Отдельной функцией, чтобы клетки таблицы
 *  утверждали состояние, а не букву: дословная сверка в каждой клетке
 *  краснела бы на верной переформулировке. */
export function текстИсхода(исход: Исход): string | null {
  switch (исход) {
    case 'отказ':
      return 'Удалить не получилось: сервер отказал. Учётная запись и занятия на месте.';
    case 'неизвестно':
      return 'Не удалось узнать, удалена ли запись. Мы проверим это, когда вы снова откроете родительский раздел.';
    case 'данные-остались':
      return 'Учётная запись удалена, но данные на этом устройстве могли остаться. Перезагрузите страницу — мы допишем удаление.';
    case 'жива':
      return 'Учётная запись не удалена: сервер её сохранил. Можно попробовать снова.';
    case 'незавершено':
      return 'Удаление учётной записи не завершилось: неизвестно, удалена ли она. Войдите, чтобы проверить.';
    case 'не-стёрто':
      return 'Учётная запись удалена, а стереть её занятия с этого устройства не вышло. Пока они не стёрты, приложение не откроется.';
    case 'удалено':
    case 'нечего':
      return null;
  }
}

/** Сколько ждать ответа серверной функции.
 *
 *  Без предела оборванный запрос держал бы окно пароля занятым сколь
 *  угодно долго. По истечении `invoke` сам обрывает запрос и возвращает
 *  `{ error }` вида `FunctionsFetchError` — прочитано в
 *  `node_modules/@supabase/functions-js/dist/main/FunctionsClient.js`. */
export const ПРЕДЕЛ_ОТВЕТА_МС = 20_000;

/** Отказал ли сервер ДО удаления.
 *
 *  Только ответ 400–499. Серверная функция
 *  (`supabase/functions/delete-account/handler.ts`) всё, что отвергает,
 *  отвергает до `deleteUser`: нет токена, токен не принят, токен не свежий,
 *  не тот метод. А 500 и выше приходят и от шлюза перед функцией — 504
 *  может прийти уже ПОСЛЕ удаления, и назвать его отказом значило бы
 *  сказать «запись на месте» о записи, которой нет. Всё прочее — сеть,
 *  истёкший предел, отказ промежуточного узла, ответ, который не
 *  разобрался, — исход неизвестен. */
function отказДоУдаления(ошибка: unknown): boolean {
  if (!(ошибка instanceof FunctionsHttpError)) return false;
  const статус = (ошибка.context as { status?: unknown } | undefined)?.status;
  return typeof статус === 'number' && статус >= 400 && статус < 500;
}

/** Выход без проверки ответа: к этому моменту записи на сервере нет, и
 *  отказ выхода ничего не меняет. Бросок — сбой хранилища библиотеки —
 *  тоже не повод останавливаться, но о нём надо знать. */
async function выйти(область: 'global' | 'local'): Promise<void> {
  try {
    await supabase().auth.signOut({ scope: область });
  } catch (сбой) {
    if (!looksOffline(сбой)) report(сбой, 'accountDeletion:выход');
  }
}

/** Шаги 4–6: сервер подтвердил удаление.
 *
 *  Порядок несущий, и он измерен: стирание — ДО выхода. Выход шлёт
 *  `SIGNED_OUT`, шлюз входа уходит на «Войдите снова», родительский раздел
 *  размонтируется — и отказ стирания, случившийся после выхода, до
 *  родителя уже не доходит. Поэтому при отказе стирания здесь НЕ выходим:
 *  раздел остаётся на экране, а пометка `'confirmed'` доводит уборку при
 *  следующем запуске. */
async function довестиПодтверждённое(): Promise<Исход> {
  await записатьПометку('confirmed');
  try {
    await wipeDevice();
  } catch (сбой) {
    report(сбой, 'accountDeletion:стирание');
    return 'данные-остались';
  }
  await снятьПометку();
  await выйти('global');
  return 'удалено';
}

/** Удаление по кнопке — шаги 1–6. Зовётся ПОСЛЕ того, как пароль
 *  подтверждён сервером. */
export async function удалитьУчётнуюЗапись(): Promise<Исход> {
  await записатьПометку('started');

  /** Предел — присваиванием свойства, а не ключом литерала. Сторож единого
   *  списка сетевых признаков (`tests/data/offline.test.ts`) считает голое
   *  слово-признак в коде вторым списком, а обращение через точку
   *  пропускает намеренно. Здесь это имя параметра библиотеки. */
  const параметры: FunctionInvokeOptions = {};
  параметры.timeout = ПРЕДЕЛ_ОТВЕТА_МС;

  let ошибка: unknown;
  try {
    ({ error: ошибка } = await supabase().functions.invoke('delete-account', параметры));
  } catch (бросок) {
    /** `invoke` по устройству не бросает. Бросок значит, что неизвестно
     *  даже, ушёл ли запрос, — то есть исход неизвестен. */
    ошибка = бросок ?? new Error('invoke бросил пустое значение');
  }

  if (ошибка) {
    if (отказДоУдаления(ошибка)) {
      /** Пароль только что подтверждён, токен свежий — отказ здесь не
       *  ожидается, и о нём надо знать. */
      report(ошибка, 'accountDeletion:отказ');
      await снятьПометку();
      return 'отказ';
    }
    if (!looksOffline(ошибка)) report(ошибка, 'accountDeletion:неизвестно');
    return 'неизвестно';
  }

  return довестиПодтверждённое();
}

/** Шаг 7: повтор уборки при запуске, НАД шлюзом входа.
 *
 *  Не зависит от сессии, и в этом весь смысл места: после удаления записи
 *  сессии нет или она мертва, шлюз входа показывает экран входа, и всё, что
 *  стоит под ним, не монтируется вовсе. Возвращает `'не-стёрто'`, если
 *  стереть снова не вышло: тогда приложение не отрисовывается — под ним
 *  лежат занятия удалённой семьи. */
export async function довестиУдаление(): Promise<'нечего' | 'не-стёрто'> {
  if ((await прочитатьПометку()) !== 'confirmed') return 'нечего';
  try {
    await wipeDevice();
  } catch (сбой) {
    report(сбой, 'accountDeletion:повтор');
    return 'не-стёрто';
  }
  await снятьПометку();
  /** Сессия удалённого пользователя мертва; снимается только здесь. */
  await выйти('local');
  return 'нечего';
}

/** Шаги 8–9: родительский раздел открыт, а удаление кончилось неизвестно.
 *
 *  Проверяется здесь, а не только при запуске: слова шага 3 обещают
 *  проверку при следующем открытии раздела. `getUser()` может БРОСИТЬ не
 *  `AuthError` (библиотека перебрасывает всё прочее), поэтому обёрнуто.
 *
 *  Код `user_not_found` для удалённого пользователя — поведение GoTrue, не
 *  проверенное исполнением: для этого нужен вход. Если код окажется иным,
 *  пометка просто останется, и родитель увидит «не завершилось» снова —
 *  ошибиться в сторону «удалено» здесь нельзя. */
export async function проверитьУдаление(естьСессия: boolean): Promise<Исход> {
  if ((await прочитатьПометку()) !== 'started') return 'нечего';
  if (!естьСессия) return 'незавершено';
  switch (await спроситьСервер()) {
    case 'удалена':
      return довестиПодтверждённое();
    case 'жива':
      await снятьПометку();
      return 'жива';
    case 'без-ответа':
      return 'нечего';
  }
}

/** Что сервер говорит о записи, от чьего имени открыта сессия.
 *
 *  `getUser()` может БРОСИТЬ не `AuthError` (библиотека перебрасывает всё
 *  прочее), поэтому обёрнуто: бросок — это «без ответа», а не «удалена».
 *  Ошибиться в сторону «удалена» здесь нельзя — за этим следует стирание. */
async function спроситьСервер(): Promise<'удалена' | 'жива' | 'без-ответа'> {
  let ответ: Awaited<ReturnType<ReturnType<typeof supabase>['auth']['getUser']>>;
  try {
    ответ = await supabase().auth.getUser();
  } catch (бросок) {
    report(бросок, 'accountDeletion:проверка');
    return 'без-ответа';
  }
  if (ответ.error) {
    return (ответ.error as { code?: unknown }).code === 'user_not_found' ? 'удалена' : 'без-ответа';
  }
  return ответ.data.user ? 'жива' : 'без-ответа';
}

/** Карточка 3в: вторая попытка удаления после неизвестного исхода.
 *
 *  Окно пароля после шага 3 остаётся на экране со своей кнопкой. Если
 *  сервер на деле удалил запись, повторное нажатие упиралось в проверку
 *  пароля под удалённой записью, и родитель слышал «Неверная почта или
 *  пароль.» — «не получилось» о том, что получилось (измерено исполнением
 *  при карточке 3а). Поэтому при пометке `'started'` исход выясняется
 *  ПЕРВЫМ, до пароля, — тем же вопросом, что и при открытии раздела:
 *
 *  - удалена → уборка и выход (шаги 4–6 таблицы 3а);
 *  - жива → пометка снимается, и дальше обычное удаление с паролем
 *    (`'продолжать'`);
 *  - без ответа → пометка остаётся, слова шага 3, пароль не спрашивается:
 *    спрашивать его под записью, которой, может быть, уже нет, — ровно та
 *    ловушка, которую это закрывает.
 *
 *  Пометка `'confirmed'` (карточка 3г): шаг 6 — запись удалена, стирание
 *  отказало — тоже оставляет окно с кнопкой, и второе нажатие без
 *  перезагрузки шло прямо в проверку пароля под удалённой записью:
 *  «Неверная почта или пароль.», а слова «перезагрузите страницу» уходили с
 *  экрана. Измерено исполнением. Здесь исход ИЗВЕСТЕН — сервер уже сказал
 *  «удалено», — и спрашивать его снова незачем: сразу повтор уборки
 *  (шаги 4–6), пароль не спрашивается.
 *
 *  Без пометки — `'продолжать'`: обычное удаление с паролем. */
export async function проверитьПередПовтором(): Promise<Исход | 'продолжать'> {
  const пометка = await прочитатьПометку();
  if (пометка === 'confirmed') return довестиПодтверждённое();
  if (пометка !== 'started') return 'продолжать';
  switch (await спроситьСервер()) {
    case 'удалена':
      return довестиПодтверждённое();
    case 'жива':
      await снятьПометку();
      return 'продолжать';
    case 'без-ответа':
      return 'неизвестно';
  }
}
